import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { compact, concat, filter, find, flow, includes, isEmpty, isFunction, isNil, map } from 'lodash/fp';
import { connect, useDispatch, useSelector } from 'react-redux';
import { H1, P, Wrapper } from '@ahmdigital/ui';
import { PARENT_PRODUCT, PRODUCT_TYPE } from '@ahmdigital/constants';
import getParentProductId from '@ahmdigital/logic/lib/utils/get-parent-product-id';
import React, { useCallback, useEffect, useState } from 'react';

import { BestSellerScaleType, ProductType } from '../../../../types/product';
import { Customer } from '../../../../selectors/select-customer/types';
import { DisclaimerWrapper, HeaderWrapper, StyledButton } from './styles';
import { DRAWER_IDS } from '../../../../ahm-constants/ui';
import { EVENTS } from '../../../../analytics/google/constants';
import { RootState } from '../../../../reducers';
import { URLS } from '../../../../models/url/constants';
import analytics from '../../../../analytics';
import browserUtils from '../../../../utils/browser';
import buildYourCoverActions from '../../../../actions/build-your-cover';
import CarouselSection from './components/carousel-section';
import CustomerActions from '../../../../actions/customer';
import DrawerActions from '../../../../actions/drawer';
import getCustomerHasChanged from '../../../../utils/customer-has-changed';
import getFutureDate from '../../../../utils/get-future-date';
import getSelector from '../../utils/get-selector';
import getUnavailableProductProps from '../../utils/get-unavailable-product-props';
import LoadingSpinner from '../../../../components/loading-spinner';
import PriceActions from '../../../../actions/price';
import PriceDisclaimer from '../../../../components/price-disclaimer';
import PricingDrawer from '../../../../components/pricing-drawer';
import ProductService from '../../../../models/product';
import RedirectAdapter from '../../../../utils/redirect-adapter';
import refineProductsByParentId from '../../utils/refine-products-by-parent-id';
import selectCustomer from '../../../../selectors/select-customer';
import selectProducts from '../../../../selectors/select-products';
import StepFooterNavigation from '../../components/step-footer-navigation';
import useGetSuitableProductIds from '../../utils/use-get-suitable-products';
import ValidationAlert from './components/validation-alert';

type ChooseYourCoverProps = {
  actions: any;
  customer: {
    frequency: Customer['frequency'];
    hospitalExcess: Customer['hospitalExcess'];
    scale: BestSellerScaleType;
    members: Customer['members'];
  };
  products: Record<string, ProductType>;
  selectedProducts: {
    extras: ProductType['id'];
    hospital: ProductType['id'];
  };
  useFutureEffectiveDate: boolean;
};

const { EXTRAS, HOSPITAL } = PRODUCT_TYPE;

const ChooseYourCover = ({
  actions,
  customer,
  products,
  selectedProducts,
  useFutureEffectiveDate,
}: ChooseYourCoverProps) => {
  const dispatch = useDispatch();
  const windowService = browserUtils.getWindow();

  const { hospitalExcess, scale } = customer;
  const customerHasChanged = getCustomerHasChanged(customer, useFutureEffectiveDate);

  const hospitalSectionRef = React.createRef<HTMLDivElement>();
  const extrasSectionRef = React.createRef<HTMLDivElement>();

  const [carouselStuck, setCarouselStuck] = useState<string | null>(null);

  const [hospitalProducts, setHospitalProducts] = useState<ProductType[]>([]);

  const { selectedServices } = useSelector((state: RootState) => state.buildYourCover);
  const {
    extrasProductIds,
    hasExtrasBestSellerWithFewerInclusions,
    hasHospitalBestSellerWithFewerInclusions,
    hasError,
    isLoading,
    hospitalParentProductIds,
  }: {
    extrasProductIds: ProductType['id'][];
    hasExtrasBestSellerWithFewerInclusions: boolean;
    hasHospitalBestSellerWithFewerInclusions: boolean;
    hasError: boolean;
    isLoading: boolean;
    hospitalParentProductIds: ProductType['parentProductId'][];
  } = useGetSuitableProductIds({
    customer,
    extrasServices: selectedServices.extras,
    hospitalServices: selectedServices.hospital,
  });
  const extrasProducts = useSelector((state) => getSelector(extrasProductIds, state)) as ProductType[];
  const hasBestSellerWithFewerInclusions =
    hasExtrasBestSellerWithFewerInclusions || hasHospitalBestSellerWithFewerInclusions;

  // this returns the actual products that match the product ids
  const allHospitalProducts: ProductType[] = flow(
    filter((product: ProductType) => includes(product.parentProductId, hospitalParentProductIds)),
    map((product) => ({
      ...product,
      unavailableWithExcessInfo: getUnavailableProductProps(
        actions.customer.changeHospitalExcess,
        hospitalExcess,
        product,
      ),
    })),
    compact,
  )(products);

  const refinedProducts = refineProductsByParentId(hospitalParentProductIds, allHospitalProducts);
  const productsAvailableByScale = filter(
    (product: ProductType) => includes(customer.scale, product.scales?.supported),
    refinedProducts,
  ) as ProductType[];

  const productIds = map('id', concat(extrasProducts, hospitalProducts));
  const stringedHospitalAndExtrasProducts = JSON.stringify([extrasProducts, hospitalProducts]);

  useEffect(() => {
    setHospitalProducts(productsAvailableByScale);
  }, [
    customerHasChanged,
    hospitalExcess,
    JSON.stringify([extrasProducts, hospitalProducts, hospitalParentProductIds]),
  ]);

  useEffect(() => {
    actions.price.load(productIds, customer, { date: getFutureDate(useFutureEffectiveDate) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHasChanged, hospitalExcess, useFutureEffectiveDate, JSON.stringify([extrasProducts, hospitalProducts])]);

  const { hospital: selectedHospitalProductId } = selectedProducts;
  const parentProductId = getParentProductId(selectedHospitalProductId);

  useEffect(() => {
    if (!selectedHospitalProductId || !parentProductId) return;
    const excessHasUpdated = !includes(`-${hospitalExcess}`, selectedHospitalProductId);

    if (!excessHasUpdated) return;

    const validHospitalProductIds = map('id', products);

    const updatedHospitalProduct = `${parentProductId}-excess-${hospitalExcess}`;

    const productToSetOrUnset = includes(updatedHospitalProduct, validHospitalProductIds)
      ? updatedHospitalProduct
      : selectedHospitalProductId;

    dispatch(buildYourCoverActions.setOrUnsetSelectedProduct('hospital', productToSetOrUnset));
  }, [hospitalExcess]);

  useEffect(() => {
    if (!selectedHospitalProductId || !includes(PARENT_PRODUCT.STARTER_BASIC, selectedHospitalProductId)) return;

    const selectedHospitalProduct = find({ id: selectedHospitalProductId }, products);
    const productIsAvailable = ProductService.isAvailableFor(selectedHospitalProduct as ProductType, scale);

    if (!productIsAvailable) {
      dispatch(buildYourCoverActions.setOrUnsetSelectedProduct(HOSPITAL, selectedHospitalProductId));
    }
  }, [scale]);

  useEffect(() => {
    if (!isEmpty(extrasProducts) && !isEmpty(hospitalProducts)) {
      analytics.ecommerceGA4(EVENTS.ECOMMERCE_GA4.VIEW_ITEM_LIST, {
        customer,
        products: [...hospitalProducts, ...extrasProducts],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringedHospitalAndExtrasProducts]);
  const handleScroll = () => {
    const firstHospitalProduct = document.getElementById(hospitalProducts?.[0]?.id ?? '');
    const hospitalHeadTop = firstHospitalProduct?.getBoundingClientRect()?.top;

    const firstExtrasProduct = document.getElementById(extrasProducts?.[0]?.id ?? '');
    const extrasHeadTop = firstExtrasProduct?.getBoundingClientRect()?.top;

    if (!isNil(hospitalHeadTop) || !isNil(extrasHeadTop)) {
      if (hospitalHeadTop === 0) {
        setCarouselStuck(HOSPITAL);
      } else if (extrasHeadTop === 0) {
        setCarouselStuck(EXTRAS);
      } else {
        setCarouselStuck(null);
      }
    }
  };

  useEffect(() => {
    if ('addEventListener' in windowService && isFunction(windowService.addEventListener)) {
      windowService.addEventListener('scroll', handleScroll);
    }

    return () => {
      if ('removeEventListener' in windowService && isFunction(windowService.removeEventListener)) {
        windowService.removeEventListener('scroll', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds]);

  const openChangeDetailsDrawer = useCallback(() => {
    DrawerActions.openDrawer(DRAWER_IDS.CHANGE_DETAILS)(dispatch);
  }, [dispatch]);

  if (hasError) {
    return <RedirectAdapter to={URLS.SALES.ERROR_500} />;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Wrapper size="large">
        <HeaderWrapper>
          <H1 looksLike="h2">Choose the cover that suits you</H1>
          <P variant="withoutMargin">Here are options we think you’ll like based on the inclusions you chose</P>
          {hasBestSellerWithFewerInclusions && <P>There’s also a best-seller that’s worth a look.</P>}
        </HeaderWrapper>

        <DisclaimerWrapper>
          <PriceDisclaimer
            bold
            elementType="button"
            forProducts={hospitalProducts}
            highlightIncome
            showRefineLink={false}
          />
          <StyledButton
            icon={{ name: 'pencil' }}
            onClick={openChangeDetailsDrawer}
            variant={['border', 'white', 'smallPadding']}
          >
            Edit
          </StyledButton>
        </DisclaimerWrapper>

        <ValidationAlert hospitalSectionRef={hospitalSectionRef} extrasSectionRef={extrasSectionRef} />

        <CarouselSection
          hasBestSellerWithFewerInclusions={hasHospitalBestSellerWithFewerInclusions}
          productType={HOSPITAL}
          products={hospitalProducts}
          isStuck={carouselStuck === HOSPITAL}
          sectionRef={hospitalSectionRef}
        />
        <CarouselSection
          hasBestSellerWithFewerInclusions={hasExtrasBestSellerWithFewerInclusions}
          productType={EXTRAS}
          products={extrasProducts}
          isStuck={carouselStuck === EXTRAS}
          sectionRef={extrasSectionRef}
        />
      </Wrapper>

      <StepFooterNavigation />

      <PricingDrawer isProductWithHospitalServices showIncomeOptions />
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: {
    customer: bindActionCreators(CustomerActions, dispatch),
    price: bindActionCreators(PriceActions, dispatch),
  },
});

const mapStateToProps = (state: RootState) => ({
  customer: selectCustomer(state) as ChooseYourCoverProps['customer'],
  products: selectProducts(state),
  selectedProducts: state.buildYourCover.selectedProducts,
  useFutureEffectiveDate: state.price.useFutureEffectiveDate,
});

export default connect(mapStateToProps, mapDispatchToProps)(ChooseYourCover);
export { ChooseYourCover as ChooseYourCoverForTest };
